import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Share2, ShoppingCart } from "lucide-react";
import { Avatar, Logo } from "@/components/ui";
import { useCartCount } from "@/features/cart/api";
import { LocationChip } from "@/features/locations/LocationChip";
import { NotificationBell } from "@/features/notifications/NotificationBell";
import { cn } from "@/lib/cn";
import { useAuth } from "@/stores/auth";
import { useUI } from "@/stores/ui";

/** The page title without the site suffix, following <title> changes (pages set it with Helmet). */
export function useDocumentTitle(): string {
  const read = () => (typeof document === "undefined" ? "" : document.title.replace(/\s*[·|-]\s*RealityStore$/, "").trim());
  const [title, setTitle] = useState(read);
  useEffect(() => {
    const update = () => setTitle(read());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.head, { subtree: true, childList: true, characterData: true });
    return () => observer.disconnect();
  }, []);
  return title === "RealityStore" ? "" : title;
}

/** Tab roots show a plain title (no back arrow): you get there from the tab bar. */
const TAB_ROOTS: Record<string, string> = { "/chats": "Chats", "/more": "Account" };

function CartButton() {
  const count = useCartCount();
  const setCartOpen = useUI((s) => s.setCartOpen);
  return (
    <button
      type="button"
      data-cart-target
      onClick={() => setCartOpen(true)}
      className="relative grid size-11 place-items-center rounded-full text-ink-soft active:bg-fg/5"
      aria-label={count ? `Cart, ${count} item${count === 1 ? "" : "s"}` : "Cart"}
    >
      <ShoppingCart className="size-[22px]" aria-hidden />
      {count > 0 && (
        <span className="tabular absolute right-0.5 top-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[var(--primary-strong)] px-1 text-[10px] font-semibold text-white">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}

/** Your photo (or initials) top-right on phones: opens Account; signed out it's a Log in pill. */
function ProfileButton() {
  const user = useAuth((s) => s.user);
  const authed = useAuth((s) => s.status === "authenticated");
  const { pathname } = useLocation();
  if (!authed || !user)
    return (
      <Link
        to={`/login?next=${encodeURIComponent(pathname)}`}
        className="ml-1 inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-full border border-[var(--border-glow)] px-3 text-sm font-medium text-ink"
      >
        Log in
      </Link>
    );
  return (
    <Link to="/more" aria-label={`Account: ${user.full_name}`} className="ml-1 grid size-11 place-items-center rounded-full active:bg-fg/5">
      <Avatar
        name={user.full_name}
        src={user.avatar_thumb || user.avatar || null}
        seed={user.id}
        size="sm"
        className="ring-2 ring-primary/40"
      />
    </Link>
  );
}

const COLLAPSE_AT = 220;

/** Home on phones: true once the hero search bar has scrolled out of view. */
export function useHomeCollapsed(enabled: boolean): boolean {
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    setCollapsed(false);
    if (!enabled) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setCollapsed(window.scrollY > COLLAPSE_AT));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [enabled]);
  return collapsed;
}

export function MobileHomeBar({ onSearch, collapsed }: { onSearch: () => void; collapsed: boolean }) {
  const authed = useAuth((s) => s.status === "authenticated");
  return (
    <div data-mobile-header className="flex h-[var(--header-h)] items-center gap-1.5 px-3">
      <Link to="/" aria-label="RealityStore home" className="shrink-0 rounded-lg">
        <Logo className="[&_img]:h-[26px]" />
      </Link>
      <LocationChip compact className="ml-1 h-8 px-2 text-[13px]" />
      <span className="flex-1" />
      {/* Once the page's own search bar has scrolled away, a search icon takes its place up here. */}
      {collapsed && (
        <button
          type="button"
          onClick={onSearch}
          className="grid size-10 place-items-center rounded-full text-ink-soft active:bg-fg/5"
          aria-label="Search RealityStore"
        >
          <Search className="size-[21px]" aria-hidden />
        </button>
      )}
      {authed && <NotificationBell />}
      <ProfileButton />
    </div>
  );
}

/** Inner pages on phones: back, the page title, and share / search / cart. */
export function MobileInnerBar({ onSearch }: { onSearch: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const docTitle = useDocumentTitle();
  const root = TAB_ROOTS[pathname];
  const title = root ?? docTitle;
  const browse = pathname.startsWith("/search") || pathname.startsWith("/c/");
  const back = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate("/");
  };
  const share = async () => {
    const data = { title: document.title, url: window.location.href };
    try {
      if (navigator.share) await navigator.share(data);
      else await navigator.clipboard.writeText(data.url);
    } catch {
      /* cancelled */
    }
  };
  const canShare = /^\/(p|store|deals|c)\//.test(pathname);
  return (
    <div className="container-rs flex h-[var(--header-h)] items-center gap-1">
      {!root && (
        <button
          type="button"
          onClick={back}
          className="-ml-2 grid size-11 shrink-0 place-items-center rounded-full text-ink active:bg-fg/5"
          aria-label="Back"
        >
          <ArrowLeft className="size-6" aria-hidden />
        </button>
      )}
      <p className={cn("min-w-0 flex-1 truncate font-display font-semibold", root ? "text-xl" : "text-[17px]")}>{title}</p>
      {browse && (
        <button
          type="button"
          onClick={onSearch}
          className="grid size-11 place-items-center rounded-full text-ink-soft active:bg-fg/5"
          aria-label="Search"
        >
          <Search className="size-[22px]" aria-hidden />
        </button>
      )}
      {canShare && (
        <button
          type="button"
          onClick={() => void share()}
          className="grid size-11 place-items-center rounded-full text-ink-soft active:bg-fg/5"
          aria-label="Share"
        >
          <Share2 className="size-[21px]" aria-hidden />
        </button>
      )}
      <CartButton />
    </div>
  );
}
